import { Button, Col, Divider, Modal, Row, message } from "antd";
import Draggable from "react-draggable";
import React, { useEffect, useRef, useState } from "react";
import { getAllConnection } from "../../../service/db.service";
import FileParameters from "./FileParameters";
import DatabaseParameters from "./DatabaseParameters";

// ─── Input location options ───────────────────────────────────
const FILE_LOCATION_OPTS = [
  { value: "Environment location", label: "Environment location" },
  { value: "From previous steps", label: "From previous steps" },
];

// ─── Section heading ──────────────────────────────────────────
function SectionDivider({ label }) {
  return (
    <Row style={{ margin: "12px 0 4px" }}>
      <Col span={24}>
        <Divider style={{ fontSize: 13, color: "#0B1F3A", fontWeight: 500 }}>
          {label}
        </Divider>
      </Col>
    </Row>
  );
}

// ─── Sub-category parameter forms ────────────────────────────
// Each receives: { shared, vars, onChange, subCategory }
// shared = { environments, connections, folderPaths, steps, defaultEnvironment }
// vars   = current variable state
// onChange(patch) merges into vars

function FileToSpecificationForm({ shared, vars, onChange }) {
  return (
    <FileParameters
      inputLocationOptions={FILE_LOCATION_OPTS}
      {...shared}
      value={{
        inputLocation: vars.inputLocationOne,
        environment: vars.environmentOne,
        inputFrom: vars.inputFromOne,
      }}
      onChange={(p) =>
        onChange({
          inputLocationOne: p.inputLocation ?? vars.inputLocationOne,
          environmentOne: p.environment ?? vars.environmentOne,
          inputFromOne: p.inputFrom ?? vars.inputFromOne,
        })
      }
    />
  );
}

function FileToDatabaseForm({ shared, vars, onChange }) {
  return (
    <>
      <SectionDivider label="File Parameters" />
      <FileParameters
        inputLocationOptions={FILE_LOCATION_OPTS}
        {...shared}
        value={{
          inputLocation: vars.inputLocationOne,
          environment: vars.environmentOne,
          inputFrom: vars.inputFromOne,
        }}
        onChange={(p) =>
          onChange({
            inputLocationOne: p.inputLocation ?? vars.inputLocationOne,
            environmentOne: p.environment ?? vars.environmentOne,
            inputFromOne: p.inputFrom ?? vars.inputFromOne,
          })
        }
      />
      <SectionDivider label="Database Parameters" />
      <DatabaseParameters
        environments={shared.environments}
        connections={shared.connections}
        value={{ environment: vars.environmentTwo, database: vars.databaseOne }}
        onChange={(p) =>
          onChange({
            environmentTwo: p.environment ?? vars.environmentTwo,
            databaseOne: p.database ?? vars.databaseOne,
          })
        }
      />
    </>
  );
}

function DatabaseToDatabaseForm({ shared, vars, onChange }) {
  return (
    <>
      <SectionDivider label="Source Database" />
      <DatabaseParameters
        environments={shared.environments}
        connections={shared.connections}
        value={{ environment: vars.environmentOne, database: vars.databaseOne }}
        onChange={(p) =>
          onChange({
            environmentOne: p.environment ?? vars.environmentOne,
            databaseOne: p.database ?? vars.databaseOne,
          })
        }
      />
      <SectionDivider label="Target Database" />
      <DatabaseParameters
        environments={shared.environments}
        connections={shared.connections}
        value={{ environment: vars.environmentTwo, database: vars.databaseTwo }}
        onChange={(p) =>
          onChange({
            environmentTwo: p.environment ?? vars.environmentTwo,
            databaseTwo: p.database ?? vars.databaseTwo,
          })
        }
      />
    </>
  );
}

function FileToFileForm({ shared, vars, onChange }) {
  return (
    <>
      <SectionDivider label="File One Parameters" />
      <FileParameters
        inputLocationOptions={FILE_LOCATION_OPTS}
        {...shared}
        value={{
          inputLocation: vars.inputLocationOne,
          environment: vars.environmentOne,
          inputFrom: vars.inputFromOne,
        }}
        onChange={(p) =>
          onChange({
            inputLocationOne: p.inputLocation ?? vars.inputLocationOne,
            environmentOne: p.environment ?? vars.environmentOne,
            inputFromOne: p.inputFrom ?? vars.inputFromOne,
          })
        }
      />
      <SectionDivider label="File Two Parameters" />
      <FileParameters
        inputLocationOptions={FILE_LOCATION_OPTS}
        {...shared}
        value={{
          inputLocation: vars.inputLocationTwo,
          environment: vars.environmentTwo,
          inputFrom: vars.inputFromTwo,
        }}
        onChange={(p) =>
          onChange({
            inputLocationTwo: p.inputLocation ?? vars.inputLocationTwo,
            environmentTwo: p.environment ?? vars.environmentTwo,
            inputFromTwo: p.inputFrom ?? vars.inputFromTwo,
          })
        }
      />
    </>
  );
}

function FileMoveForm({ shared, vars, onChange }) {
  return (
    <>
      <SectionDivider label="Source Location" />
      <FileParameters
        inputLocationOptions={FILE_LOCATION_OPTS}
        {...shared}
        value={{
          inputLocation: vars.inputLocationOne,
          environment: vars.environmentOne,
          inputFrom: vars.inputFromOne,
        }}
        onChange={(p) =>
          onChange({
            inputLocationOne: p.inputLocation ?? vars.inputLocationOne,
            environmentOne: p.environment ?? vars.environmentOne,
            inputFromOne: p.inputFrom ?? vars.inputFromOne,
          })
        }
      />
      <SectionDivider label="Destination Location" />
      <FileParameters
        inputLocationOptions={FILE_LOCATION_OPTS}
        {...shared}
        value={{
          inputLocation: vars.inputLocationTwo,
          environment: vars.environmentTwo,
          inputFrom: vars.inputFromTwo,
        }}
        onChange={(p) =>
          onChange({
            inputLocationTwo: p.inputLocation ?? vars.inputLocationTwo,
            environmentTwo: p.environment ?? vars.environmentTwo,
            inputFromTwo: p.inputFrom ?? vars.inputFromTwo,
          })
        }
      />
    </>
  );
}

function FileExtractForm({ shared, vars, onChange }) {
  return (
    <FileParameters
      inputLocationOptions={FILE_LOCATION_OPTS}
      {...shared}
      value={{
        inputLocation: vars.inputLocationOne,
        environment: vars.environmentOne,
        inputFrom: vars.inputFromOne,
      }}
      onChange={(p) =>
        onChange({
          inputLocationOne: p.inputLocation ?? vars.inputLocationOne,
          environmentOne: p.environment ?? vars.environmentOne,
          inputFromOne: p.inputFrom ?? vars.inputFromOne,
        })
      }
    />
  );
}

function EventMonitorForm({ shared, vars, onChange, eventType }) {
  // eventType comes from taskFileParameters — "SQL Event" or "File received in a location"
  const isSql = (eventType || "").toLowerCase().includes("sql");
  if (isSql) {
    return (
      <>
        <Row style={{ marginBottom: 8 }}>
          <Col span={6}>
            <p className="spec-modal-label">Event Type</p>
          </Col>
          <Col span={18}>
            <p style={{ paddingTop: 5, color: "#666" }}>SQL Event</p>
          </Col>
        </Row>
        <SectionDivider label="Database Connection" />
        <DatabaseParameters
          environments={shared.environments}
          connections={shared.connections}
          value={{
            environment: vars.environmentTwo,
            database: vars.databaseOne,
          }}
          onChange={(p) =>
            onChange({
              environmentTwo: p.environment ?? vars.environmentTwo,
              databaseOne: p.database ?? vars.databaseOne,
            })
          }
        />
      </>
    );
  }
  // File event — pick up where file arrives
  return (
    <>
      <Row style={{ marginBottom: 8 }}>
        <Col span={6}>
          <p className="spec-modal-label">Event Type</p>
        </Col>
        <Col span={18}>
          <p style={{ paddingTop: 5, color: "#666" }}>
            File received in a location
          </p>
        </Col>
      </Row>
      <SectionDivider label="File Location" />
      <FileParameters
        inputLocationOptions={FILE_LOCATION_OPTS}
        {...shared}
        value={{
          inputLocation: vars.inputLocationOne,
          environment: vars.environmentOne,
          inputFrom: vars.inputFromOne,
        }}
        onChange={(p) =>
          onChange({
            inputLocationOne: p.inputLocation ?? vars.inputLocationOne,
            environmentOne: p.environment ?? vars.environmentOne,
            inputFromOne: p.inputFrom ?? vars.inputFromOne,
          })
        }
      />
    </>
  );
}

// ─── Validation rules per sub-category ───────────────────────
const VALIDATORS = {
  "File to Specification": (v) => {
    if (!v.inputLocationOne) return "Input Location is required";
    if (v.inputLocationOne === "Environment location" && !v.environmentOne)
      return "Environment is required";
    if (!v.inputFromOne) return "Input from is required";
    return null;
  },
  "File to Database": (v) => {
    if (!v.inputLocationOne) return "File Input Location is required";
    if (v.inputLocationOne === "Environment location" && !v.environmentOne)
      return "File Environment is required";
    if (!v.inputFromOne) return "File input from is required";
    if (!v.environmentTwo) return "Database Environment is required";
    if (!v.databaseOne) return "Database is required";
    return null;
  },
  "Database to Database": (v) => {
    if (!v.environmentOne || !v.databaseOne)
      return "Source database is required";
    if (!v.environmentTwo || !v.databaseTwo)
      return "Target database is required";
    return null;
  },
  "File to File": (v) => {
    if (!v.inputLocationOne) return "File One Input Location is required";
    if (!v.inputFromOne) return "File One input from is required";
    if (!v.inputLocationTwo) return "File Two Input Location is required";
    if (!v.inputFromTwo) return "File Two input from is required";
    return null;
  },
  "File Move, Copy or Delete": (v) => {
    if (!v.inputLocationOne) return "Source Input Location is required";
    if (!v.inputFromOne) return "Source folder is required";
    if (!v.inputLocationTwo) return "Destination Input Location is required";
    if (!v.inputFromTwo) return "Destination folder is required";
    return null;
  },
  "Extract Compressed File": (v) => {
    if (!v.inputLocationOne) return "Input Location is required";
    if (v.inputLocationOne === "Environment location" && !v.environmentOne)
      return "Environment is required";
    if (!v.inputFromOne) return "Input from is required";
    return null;
  },
  "Event Monitor": (v, eventType) => {
    const isSql = (eventType || "").toLowerCase().includes("sql");
    if (isSql) {
      if (!v.environmentTwo) return "Database Environment is required";
      if (!v.databaseOne) return "Database is required";
    } else {
      if (!v.inputLocationOne) return "Input Location is required";
      if (!v.inputFromOne) return "Input from is required";
    }
    return null;
  },
};

// ─── Form registry ────────────────────────────────────────────
const FORMS = {
  "File to Specification": FileToSpecificationForm,
  "File to Database": FileToDatabaseForm,
  "Database to Database": DatabaseToDatabaseForm,
  "File to File": FileToFileForm,
  "File Move, Copy or Delete": FileMoveForm,
  "Extract Compressed File": FileExtractForm,
  "Event Monitor": EventMonitorForm,
};

// ─── Main modal ───────────────────────────────────────────────
function VariablesModal(props) {
  const {
    open,
    subCategoryValues,
    variables,
    environments,
    folderPaths,
    stepNames,
    defaultEnvironment,
    eventType,
    onSave,
    onClose,
  } = props;

  const [vars, setVars] = useState({});
  const [connections, setConnections] = useState([]);
  const [disabled, setDisabled] = useState(true);
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
  });
  const dragRef = useRef(null);

  useEffect(() => {
    setVars(variables || {});
  }, [variables, open]);

  useEffect(() => {
    getAllConnection().then((res) => setConnections(res.data || []));
  }, []);

  const onStart = (_, uiData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    setBounds({
      left: -rect.left + uiData.x,
      right: clientWidth - (rect.right - uiData.x),
      top: -rect.top + uiData.y,
      bottom: clientHeight - (rect.bottom - uiData.y),
    });
  };

  const handleClose = () => {
    Modal.confirm({
      title: "You will lose all changes",
      content: "Are you sure you want to cancel?",
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        setVars(variables || {});
        onClose(false);
      },
      onCancel: () => onClose(true),
    });
  };

  const handleSave = () => {
    const validator = VALIDATORS[subCategoryValues];
    if (validator) {
      const err = validator(vars, eventType);
      if (err) {
        message.error(err);
        return;
      }
    }
    onSave(vars);
  };

  const handleChange = (patch) => {
    setVars((prev) => ({ ...prev, ...patch }));
  };

  const FormComponent = FORMS[subCategoryValues];
  const shared = {
    environments: environments || [],
    connections,
    folderPaths: folderPaths || [],
    steps: stepNames || [],
    defaultEnvironment,
  };

  return (
    <Modal
      destroyOnClose
      width="52%"
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="save" onClick={handleSave}>
          Save
        </Button>,
        <Button key="cancel" type="primary" onClick={handleClose}>
          Cancel
        </Button>,
      ]}
      modalRender={(modal) => (
        <Draggable disabled={disabled} bounds={bounds} onStart={onStart}>
          <div ref={dragRef}>{modal}</div>
        </Draggable>
      )}
      title={
        <div
          style={{ width: "100%", cursor: "move" }}
          onMouseOver={() => setDisabled(false)}
          onMouseOut={() => setDisabled(true)}
        >
          Parameters
        </div>
      }
    >
      <div style={{ maxHeight: "78vh", overflowY: "auto", paddingRight: 4 }}>
        <Row style={{ paddingBottom: 16 }}>
          <p className="modal-header-text">Add / View Parameters</p>
        </Row>

        {/* Sub-category badge */}
        <Row style={{ marginBottom: 16 }}>
          <Col span={6}>
            <p className="spec-modal-label">Sub Category</p>
          </Col>
          <Col span={18}>
            <span
              style={{
                display: "inline-block",
                padding: "2px 12px",
                background: "#E6F1FB",
                color: "#185FA5",
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {subCategoryValues}
            </span>
          </Col>
        </Row>

        {/* Render the correct form */}
        {FormComponent ? (
          <FormComponent
            shared={shared}
            vars={vars}
            onChange={handleChange}
            eventType={eventType}
          />
        ) : (
          <div
            style={{ color: "#888", padding: "20px 0", textAlign: "center" }}
          >
            No parameters required for this step type.
          </div>
        )}
      </div>
    </Modal>
  );
}

export default VariablesModal;
